'use client';

import { useIntl } from '@capsicum/common';
import { Alert, Button } from '@capsicum/common-web';
import { AlertIcon } from '@capsicum/common-web/icons';
import { useExportAndCloseAccountIntro } from '@capsicum/features/account/shared';
import { type ReactNode } from 'react';
import styled from 'styled-components';

type ExportAndCloseAccountIntroProps = {
	onClickCancel: () => void;
	onClickExportAndClose: () => void;
};

export const ExportAndCloseAccountIntro = ({
	onClickCancel,
	onClickExportAndClose
}: ExportAndCloseAccountIntroProps) => {
	const { $t } = useIntl();
	const { intlTexts, privacyLink, supportLink } = useExportAndCloseAccountIntro();

	return (
		<S.Wrapper>
			<Alert
				closable={false}
				icon={AlertIcon}
				subTitle={intlTexts.subtitle}
				testID="settings-accountManagement-exportAndCloseAccount-alert"
				title={intlTexts.title}
				type="warning"
			/>
			<S.Container>
				<S.ContentText>
					{$t('settings.accountManagement.exportAndCloseAccountContent', {
						privacy: (content: ReactNode) => (
							<S.PolicyLink href={privacyLink} target="_blank">
								{content}
							</S.PolicyLink>
						),
						support: (content: ReactNode) => (
							<S.PolicyLink href={supportLink} target="_blank">
								{content}
							</S.PolicyLink>
						)
					})}
				</S.ContentText>
				<Button
					color="destructive"
					fullWidth
					testID="settings-accountManagement-exportAndCloseAccount-button"
					text={intlTexts.exportAndCloseAccountButton}
					onClick={onClickExportAndClose}
				/>
				<Button
					color="neutral"
					fullWidth
					testID="settings-accountManagement-cancel-button"
					text={intlTexts.cancelButton}
					variant="text"
					onClick={onClickCancel}
				/>
			</S.Container>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: ${({ theme }) => `${theme.spacings.medium} 0`};
		gap: ${({ theme }) => theme.spacings.medium};

		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small}`};
		}
	`;

	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.small};
		gap: ${({ theme }) => theme.spacings.small};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium}
	`;

	export const ContentText = styled.span`
		text-align: center;
		${({ theme }) => theme.fonts.text.text3};
		white-space: pre-line;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const PolicyLink = styled.a`
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;

	export const Title = styled.span`
		${({ theme }) => theme.fonts.text.textTopBar}
	`;

	export const Text3 = styled.span`
		white-space: pre-line;
		text-align: center;
		${({ theme }) => theme.fonts.text.text3}
	`;

	export const Bold = styled.span`
		${({ theme }) => theme.fonts.text.text1}
	`;
}
